/**
 * Serviço centralizado de Tracking para Meta Pixel e Google Analytics
 *
 * Documentação Meta Pixel:
 * https://developers.facebook.com/docs/meta-pixel/reference
 */

declare global {
  interface Window {
    dataLayer: any[];
    fbq: (...args: any[]) => void;
    gtag: (...args: any[]) => void;
  }
}

// =============================================================================
// TIPOS E INTERFACES
// =============================================================================

interface ContentItem {
  id: string;
  name?: string;
  category?: string;
  quantity?: number;
  price?: number;
}

interface PurchaseData {
  value: number;
  currency?: string;
  contentType?: string;
  contentIds?: string[];
  contents?: ContentItem[];
  numItems?: number;
  orderId?: string;
}

interface LeadData {
  value?: number;
  currency?: string;
  contentName?: string;
  contentCategory?: string;
}

interface SignUpData {
  method?: string;
  contentName?: string;
}

interface SearchData {
  searchString: string;
  contentCategory?: string;
  contentIds?: string[];
}

// =============================================================================
// SERVIÇO DE TRACKING
// =============================================================================

class TrackingService {
  private debugMode = false;

  constructor() {
    // Verificar se estamos em ambiente de desenvolvimento
    this.debugMode = window.location.hostname === 'localhost' || 
                     window.location.hostname === '127.0.0.1';
  }

  /**
   * Log de debug (apenas em desenvolvimento)
   */
  private log(message: string, data?: any) {
    if (this.debugMode) {
      console.log(`[TRACKING] ${message}`, data || '');
    }
  }

  /**
   * Verifica se o tracking está disponível
   */
  private isFbqAvailable(): boolean {
    return typeof window.fbq === 'function';
  }

  private isGtagAvailable(): boolean {
    return typeof window.gtag === 'function';
  }

  // ===========================================================================
  // EVENTOS DO META PIXEL (FACEBOOK)
  // Documentação: https://developers.facebook.com/docs/meta-pixel/reference
  // ===========================================================================

  /**
   * Evento: Visualização de Página (automático, mas pode ser chamado manualmente)
   */
  trackPageView(customData?: Record<string, any>) {
    if (this.isFbqAvailable()) {
      window.fbq('track', 'PageView', customData);
      this.log('PageView tracked', customData);
    }
    
    if (this.isGtagAvailable()) {
      window.gtag('event', 'page_view', customData);
    }
  }

  /**
   * Evento: Visualização de Conteúdo
   * Use quando: usuário visualiza página de produto/serviço específico
   */
  trackViewContent(data: {
    contentName: string;
    contentCategory?: string;
    contentIds?: string[];
    contentType?: string;
    value?: number;
    currency?: string;
  }) {
    const fbData = {
      content_name: data.contentName,
      content_category: data.contentCategory,
      content_ids: data.contentIds,
      content_type: data.contentType || 'product',
      value: data.value,
      currency: data.currency || 'BRL'
    };

    if (this.isFbqAvailable()) {
      window.fbq('track', 'ViewContent', fbData);
      this.log('ViewContent tracked', fbData);
    }

    if (this.isGtagAvailable()) {
      window.gtag('event', 'view_item', {
        item_name: data.contentName,
        item_category: data.contentCategory,
        value: data.value,
        currency: data.currency || 'BRL'
      });
    }
  }

  /**
   * Evento: Lead (Cadastro de interesse)
   * Use quando: usuário demonstra interesse (ex: clica em "Testar Grátis")
   */
  trackLead(data?: LeadData) {
    const fbData = {
      value: data?.value,
      currency: data?.currency || 'BRL',
      content_name: data?.contentName,
      content_category: data?.contentCategory
    };

    if (this.isFbqAvailable()) {
      window.fbq('track', 'Lead', fbData);
      this.log('Lead tracked', fbData);
    }

    if (this.isGtagAvailable()) {
      window.gtag('event', 'generate_lead', {
        value: data?.value,
        currency: data?.currency || 'BRL'
      });
    }
  }

  /**
   * Evento: Registro Completo
   * Use quando: usuário completa o cadastro
   */
  trackCompleteRegistration(data?: SignUpData) {
    const fbData = {
      content_name: data?.contentName || 'Cadastro Sophya',
      status: 'complete',
      ...(data?.method && { method: data.method })
    };

    if (this.isFbqAvailable()) {
      window.fbq('track', 'CompleteRegistration', fbData);
      this.log('CompleteRegistration tracked', fbData);
    }

    if (this.isGtagAvailable()) {
      window.gtag('event', 'sign_up', {
        method: data?.method || 'email'
      });
    }
  }

  /**
   * Evento: Início de Checkout / Trial
   * Use quando: usuário inicia processo de teste/compra
   */
  trackInitiateCheckout(data?: {
    value?: number;
    currency?: string;
    contentCategory?: string;
    contentIds?: string[];
    numItems?: number;
  }) {
    const fbData = {
      value: data?.value || 0,
      currency: data?.currency || 'BRL',
      content_category: data?.contentCategory,
      content_ids: data?.contentIds,
      num_items: data?.numItems || 1
    };

    if (this.isFbqAvailable()) {
      window.fbq('track', 'InitiateCheckout', fbData);
      this.log('InitiateCheckout tracked', fbData);
    }

    if (this.isGtagAvailable()) {
      window.gtag('event', 'begin_checkout', {
        value: data?.value,
        currency: data?.currency || 'BRL'
      });
    }
  }

  /**
   * Evento: Adicionar Informação de Pagamento
   * Use quando: usuário adiciona método de pagamento
   */
  trackAddPaymentInfo(data?: {
    value?: number;
    currency?: string;
    contentCategory?: string;
    contentIds?: string[];
  }) {
    const fbData = {
      value: data?.value,
      currency: data?.currency || 'BRL',
      content_category: data?.contentCategory,
      content_ids: data?.contentIds
    };

    if (this.isFbqAvailable()) {
      window.fbq('track', 'AddPaymentInfo', fbData);
      this.log('AddPaymentInfo tracked', fbData);
    }

    if (this.isGtagAvailable()) {
      window.gtag('event', 'add_payment_info', {
        value: data?.value,
        currency: data?.currency || 'BRL'
      });
    }
  }

  /**
   * Evento: Compra / Assinatura
   * Use quando: usuário completa uma compra ou assinatura
   */
  trackPurchase(data: PurchaseData) {
    const fbData = {
      value: data.value,
      currency: data.currency || 'BRL',
      content_type: data.contentType || 'product',
      content_ids: data.contentIds,
      contents: data.contents?.map(item => ({
        id: item.id,
        quantity: item.quantity || 1,
        item_price: item.price
      })),
      num_items: data.numItems || 1,
      order_id: data.orderId
    };

    if (this.isFbqAvailable()) {
      window.fbq('track', 'Purchase', fbData);
      this.log('Purchase tracked', fbData);
    }

    if (this.isGtagAvailable()) {
      window.gtag('event', 'purchase', {
        transaction_id: data.orderId,
        value: data.value,
        currency: data.currency || 'BRL',
        items: data.contents?.map(item => ({
          item_id: item.id,
          item_name: item.name,
          quantity: item.quantity,
          price: item.price
        }))
      });
    }
  }

  /**
   * Evento: Assinatura / Subscribe
   * Use quando: usuário se inscreve em um serviço recorrente
   */
  trackSubscribe(data: {
    value: number;
    currency?: string;
    predictedLtv?: number;
    planName?: string;
    planId?: string;
  }) {
    const fbData = {
      value: data.value,
      currency: data.currency || 'BRL',
      predicted_ltv: data.predictedLtv
    };

    if (this.isFbqAvailable()) {
      window.fbq('track', 'Subscribe', fbData);
      this.log('Subscribe tracked', fbData);
    }

    if (this.isGtagAvailable()) {
      window.gtag('event', 'subscribe', {
        value: data.value,
        currency: data.currency || 'BRL',
        plan_name: data.planName,
        plan_id: data.planId
      });
    }
  }

  /**
   * Evento: Início de Trial
   * Use quando: usuário inicia período de teste
   */
  trackStartTrial(data?: {
    value?: number;
    currency?: string;
    predictedLtv?: number;
    trialDays?: number;
    planName?: string;
  }) {
    const fbData = {
      value: data?.value || 0,
      currency: data?.currency || 'BRL',
      predicted_ltv: data?.predictedLtv
    };

    if (this.isFbqAvailable()) {
      window.fbq('track', 'StartTrial', fbData);
      this.log('StartTrial tracked', fbData);
    }

    if (this.isGtagAvailable()) {
      window.gtag('event', 'start_trial', {
        value: data?.value,
        currency: data?.currency || 'BRL',
        trial_days: data?.trialDays,
        plan_name: data?.planName
      });
    }
  }

  /**
   * Evento: Contato
   * Use quando: usuário entra em contato (WhatsApp, email, etc)
   */
  trackContact(method?: string) {
    if (this.isFbqAvailable()) {
      window.fbq('track', 'Contact');
      this.log('Contact tracked', { method });
    }

    if (this.isGtagAvailable()) {
      window.gtag('event', 'contact', {
        method: method || 'whatsapp'
      });
    }
  }

  /**
   * Evento: Busca
   * Use quando: usuário realiza uma busca
   */
  trackSearch(data: SearchData) {
    const fbData = {
      search_string: data.searchString,
      content_category: data.contentCategory,
      content_ids: data.contentIds
    };

    if (this.isFbqAvailable()) {
      window.fbq('track', 'Search', fbData);
      this.log('Search tracked', fbData);
    }

    if (this.isGtagAvailable()) {
      window.gtag('event', 'search', {
        search_term: data.searchString
      });
    }
  }

  /**
   * Evento: Encontrar Localização
   * Use quando: usuário busca localização de serviço
   */
  trackFindLocation() {
    if (this.isFbqAvailable()) {
      window.fbq('track', 'FindLocation');
      this.log('FindLocation tracked');
    }
  }

  /**
   * Evento: Agendar
   * Use quando: usuário agenda uma consulta/reunião
   */
  trackSchedule(data?: {
    appointmentType?: string;
    value?: number;
  }) {
    if (this.isFbqAvailable()) {
      window.fbq('track', 'Schedule');
      this.log('Schedule tracked', data);
    }

    if (this.isGtagAvailable()) {
      window.gtag('event', 'schedule_appointment', {
        appointment_type: data?.appointmentType,
        value: data?.value
      });
    }
  }

  // ===========================================================================
  // EVENTOS CUSTOMIZADOS
  // ===========================================================================

  /**
   * Evento customizado para Meta Pixel
   */
  trackCustomEvent(eventName: string, data?: Record<string, any>) {
    if (this.isFbqAvailable()) {
      window.fbq('trackCustom', eventName, data);
      this.log(`Custom event "${eventName}" tracked`, data);
    }

    if (this.isGtagAvailable()) {
      window.gtag('event', eventName, data);
    }
  }

  // ===========================================================================
  // EVENTOS ESPECÍFICOS DO SOPHYA
  // ===========================================================================

  /**
   * Visualização da Landing Page
   */
  trackLandingPageView() {
    this.trackViewContent({
      contentName: 'Landing Page Sophya',
      contentCategory: 'Marketing',
      contentType: 'page'
    });
  }

  /**
   * Clique no botão "Ver Planos"
   */
  trackViewPlans() {
    this.trackCustomEvent('ViewPlans', {
      content_name: 'Seção de Preços'
    });
  }

  /**
   * Clique no botão "Testar Grátis"
   */
  trackClickTrialButton(planName: string) {
    this.trackLead({
      contentName: `Trial - ${planName}`,
      contentCategory: 'Planos'
    });
  }

  /**
   * Início do cadastro
   */
  trackStartRegistration(type: 'individual' | 'student' | 'clinic') {
    this.trackInitiateCheckout({
      contentCategory: `Cadastro ${type}`,
      numItems: 1
    });
  }

  /**
   * Cadastro completado com sucesso
   */
  trackRegistrationComplete(data: {
    type: 'individual' | 'student' | 'clinic';
    planName?: string;
    trialDays?: number;
  }) {
    // Evento de registro completo
    this.trackCompleteRegistration({
      method: 'email',
      contentName: `Cadastro ${data.type}`
    });

    // Evento de início de trial
    this.trackStartTrial({
      trialDays: data.trialDays || 30,
      planName: data.planName || 'Trial'
    });
  }

  /**
   * Clique no WhatsApp
   */
  trackWhatsAppClick(context: string) {
    this.trackContact('whatsapp');
    this.trackCustomEvent('WhatsAppClick', {
      context: context
    });
  }

  /**
   * Pagamento iniciado
   */
  trackPaymentStarted(data: {
    planName: string;
    planId: string;
    value: number;
    billingPeriod: 'monthly' | 'yearly';
  }) {
    this.trackAddPaymentInfo({
      value: data.value,
      contentCategory: data.planName,
      contentIds: [data.planId]
    });
  }

  /**
   * Assinatura completada
   */
  trackSubscriptionComplete(data: {
    planName: string;
    planId: string;
    value: number;
    billingPeriod: 'monthly' | 'yearly';
    orderId?: string;
  }) {
    // Evento de compra
    this.trackPurchase({
      value: data.value,
      contentType: 'subscription',
      contentIds: [data.planId],
      contents: [{
        id: data.planId,
        name: data.planName,
        quantity: 1,
        price: data.value
      }],
      orderId: data.orderId
    });

    // Evento de assinatura
    this.trackSubscribe({
      value: data.value,
      planName: data.planName,
      planId: data.planId,
      // LTV estimado: valor mensal * 12 meses
      predictedLtv: data.billingPeriod === 'monthly' ? data.value * 12 : data.value
    });
  }

  /**
   * Login realizado
   */
  trackLogin(method: string = 'email') {
    if (this.isGtagAvailable()) {
      window.gtag('event', 'login', { method });
    }
    this.trackCustomEvent('Login', { method });
  }

  /**
   * Nova consulta agendada
   */
  trackAppointmentScheduled(data?: {
    patientId?: string;
    value?: number;
  }) {
    this.trackSchedule({
      appointmentType: 'consultation',
      value: data?.value
    });
  }

  /**
   * Uso de funcionalidade específica
   */
  trackFeatureUsage(featureName: string, details?: Record<string, any>) {
    this.trackCustomEvent('FeatureUsage', {
      feature_name: featureName,
      ...details
    });
  }
}

// Exportar instância única (singleton)
export const trackingService = new TrackingService();
export default trackingService;
